import { createAsyncThunk } from "@reduxjs/toolkit";

import logger from "Libs/logger";
import { selectCurrentUserUUID } from "Reducers/app/selectors";
import { selectUserProfileByMeUsername } from "Reducers/profile";

import { CreateOrganizationNamespace } from "../constants";
import { loadOrganizations } from "./loadOrganizations.thunk";

import type { Organization, APIObject } from "@packages/client";
import type { AsyncThunkOptionType } from "Reducers/types";
import { billingSystemWizardOpenBasedOnEntities } from "Reducers/billingSystem";

type CreateOrganizationParams = {
  label: string;
  type?: "flexible" | "fixed";
};

/**
 * @deprecated Use createOrganizationFromLabel instead. This thunk is kept for
 * backwards compatibility with existing code that passes the full organization object.
 */
export const createOrganization = createAsyncThunk<
  Organization,
  { organization: APIObject; redirectTo?: boolean },
  AsyncThunkOptionType
>(
  "app/organizations/create_organization",
  async ({ organization }, { rejectWithValue }) => {
    try {
      const platformLib = await import("Libs/platform");
      const client = platformLib.default;

      const newOrganizationReference =
        await client.createOrganization(organization);

      const newOrganization: Organization =
        await newOrganizationReference.getEntity();

      return newOrganization;
    } catch (err) {
      logger(err, {
        action: CreateOrganizationNamespace,
        organization
      });

      return rejectWithValue(typeof err === "string" ? JSON.parse(err) : err);
    }
  }
);

/**
 * Creates an organization with the given label.
 * Country is automatically retrieved from the current user's profile.
 * Also updates the organization profile with company_name and reloads organizations.
 */
export const createOrganizationFromLabel = createAsyncThunk<
  Organization,
  CreateOrganizationParams,
  AsyncThunkOptionType
>(
  "app/organizations/create_organization_from_label",
  async ({ label, type }, { getState, rejectWithValue, dispatch }) => {
    try {
      const state = getState();
      const country = selectUserProfileByMeUsername(state)?.country;
      const userId = selectCurrentUserUUID(state);

      if (!country) {
        return rejectWithValue({
          message: "User profile not loaded or country not set",
          code: "PROFILE_NOT_READY"
        });
      }

      const platformLib = await import("Libs/platform");
      const client = platformLib.default;

      const newOrganizationReference = await client.createOrganization({
        label,
        country,
        ...(type && { type })
      });
      await client.reAuthenticate();

      const newOrganization: Organization =
        await newOrganizationReference.getEntity();

      // Update organization profile with company name
      if (newOrganization.billing_legacy) {
        await client.updateOrganizationProfile(newOrganization.id, {
          company_name: label
        });
      }

      // Reload organizations list
      if (userId) {
        dispatch(loadOrganizations({ userId }));
      }

      if (!newOrganization.billing_legacy) {
        dispatch(
          billingSystemWizardOpenBasedOnEntities({
            isNewOrganization: true,
            organizationId: newOrganization.name
          })
        );
      }

      return newOrganization;
    } catch (err) {
      logger(err, {
        action: CreateOrganizationNamespace,
        label
      });

      return rejectWithValue(typeof err === "string" ? JSON.parse(err) : err);
    }
  }
);
